import type { MouseEvent } from 'react';
import { codes as key } from 'keycode';
import type {
    TFocusEventHandler,
    TChangeEventHandler,
    TKeyboardEventHandler,
} from '@alfa-bank/site-input/models';

import type { TOnAutocomplete } from '../components/autocomplete/models';
import type { ISelectActions } from '../models';
import { getCurrentIndex, getDownIndex, getUpIndex } from '../helpers';

export class SelectActions implements ISelectActions {
    cb: ISelectActions['cb'];

    refs: ISelectActions['refs'];

    state: ISelectActions['state'];

    values: ISelectActions['values'];

    constructor(properties: ISelectActions) {
        this.cb = properties.cb;
        this.refs = properties.refs;
        this.state = properties.state;
        this.values = properties.values;
    }

    /** Сделать подсказку текущим значением */
    refreshValue = (value?: string) => {
        const { current } = this.refs.inputRef;
        const { setTempValue } = this.state;
        const inputEvent = new Event('input', {
            bubbles: true,
        });

        if (!current) return;

        if (value) current.value = value;

        setTempValue('');
        current.dispatchEvent(inputEvent);
    };

    onBlurHandler: TFocusEventHandler = obj => {
        const { displayValue, selectValue } = this.values;
        const { autocompleteRef, inputRef } = this.refs;
        const { setIsOpen, setTempValue } = this.state;
        const { onBlur } = this.cb;
        const { current } = autocompleteRef;
        const relatedTarget = obj.event?.relatedTarget as Element;
        const relatedClassName = relatedTarget?.className || '';

        if (current) {
            const className = document.activeElement?.className || '';
            const focusMovedToDropdown =
                className.includes(current.className) ||
                relatedClassName.includes(current.className);

            /** Если autocomplete активен - не закрывать */
            if (focusMovedToDropdown) return;
        }

        // Если есть подсказка - добавить ее в input
        if (displayValue !== selectValue && inputRef.current) {
            this.refreshValue();
        }

        setTempValue('');
        setIsOpen(false);

        onBlur(obj);
    };

    onChangeHandler: TChangeEventHandler = ({ event }) => {
        const { name, selectValue } = this.values;
        const { setCurrentItem, setIsOpen } = this.state;
        const { onChange } = this.cb;

        if (!onChange) return;
        const { value } = event.target;

        // При изменении текста обнулять выбор
        if (value.length > selectValue.length) {
            setCurrentItem(0);
            setIsOpen(true);
            // Если длина уменьшилась - убрать выбор
        } else {
            setCurrentItem(-1);
        }
        onChange({ value, name, event });
    };

    onFocusHandler: TFocusEventHandler = event => {
        const { setIsOpen } = this.state;
        const { onFocus } = this.cb;

        setIsOpen(true);
        onFocus(event);
    };

    onClickHandler = (event: MouseEvent<HTMLInputElement>) => {
        const { setIsOpen } = this.state;
        const { displayValue, selectValue } = this.values;
        const { onClick } = this.cb;

        if (displayValue !== selectValue) this.refreshValue();

        setIsOpen(true);
        onClick(event);
    };

    onDropDownSelect: TOnAutocomplete = ({ event, value }) => {
        const { setCurrentItem, setIsOpen, setTempValue } = this.state;
        const { name, options } = this.values;
        const { onSelect } = this.cb;
        const { label } = options[value];

        if (event) event.preventDefault();

        onSelect({ ...options[value], name, value: label });

        setTempValue('');
        setCurrentItem(value);
        setIsOpen(false);
    };

    onDropdownToggle = () => {
        const { setIsOpen } = this.state;
        const { isOpen } = this.values;

        setIsOpen(!isOpen);
    };

    onKeyDown: TKeyboardEventHandler = ({ event }) => {
        const { currentItem, displayValue, isOpen, options, readOnly } = this.values;
        const { setIsOpen } = this.state;
        const { current } = this.refs.inputRef;
        let nextItem = getCurrentIndex(options, displayValue);
        const length = options[currentItem]?.label.length || 0;

        switch (event.keyCode) {
            case key.right:
            case key.left:
                this.refreshValue();
                break;
            case key.up:
                event.preventDefault();
                this.onArrow(getUpIndex(currentItem, readOnly));
                break;
            case key.down:
                event.preventDefault();
                this.onArrow(getDownIndex(options, currentItem));
                break;
            case key.tab:
                if (event.shiftKey || currentItem === -1 || !isOpen || !options.length) return;
                event.preventDefault();
                this.refreshValue(options[currentItem].label);
                setIsOpen(false);
                if (current) current.setSelectionRange(length, length);
                break;
            case key.enter:
                event.preventDefault();
                if (currentItem > -1) {
                    nextItem = currentItem;
                } else if (displayValue && !options[nextItem]) {
                    this.unshift(displayValue);
                    nextItem = 0;
                }
                // Когда нет данных - то и селект не нужен
                if (options.length) {
                    this.onDropDownSelect({
                        value: nextItem,
                        event,
                    });
                }
                break;
            case key.esc:
                setIsOpen(false);
                break;
            default:
                break;
        }
    };

    onArrow = (index: number) => {
        const { setCurrentItem, setIsOpen } = this.state;

        setCurrentItem(index);
        setIsOpen(true);
    };

    insertSuggestion = () => {
        const { currentItem, isOpen, options, selectValue } = this.values;
        const { setTempValue } = this.state;
        const { inputRef } = this.refs;
        const label = options[currentItem]?.label || '';
        const matched = selectValue === label.substr(0, selectValue.length);

        if (!isOpen || !label || currentItem < 0 || !matched) {
            setTempValue('');

            return;
        }
        const newTemp = label.substr(selectValue.length);

        setTempValue(newTemp);

        if (!inputRef.current || selectValue.length >= label.length) return;
        inputRef.current.setSelectionRange(selectValue.length, label.length, 'forward');
    };

    unshift = (label: string) => {
        const { options } = this.values;
        const id = options[0]?.id || 0;

        options.unshift({
            label,
            id: Number(id) - 1,
            gender: 'UNKNOWN',
        });
    };

    /** Подставлять текущее значение как подсказку */
    insertCurrent = (appendCurrent?: boolean) => {
        const { displayValue, isOpen, options, selectValue } = this.values;
        const { setCurrentItem } = this.state;

        if (!isOpen) {
            const nextItem = getCurrentIndex(options, selectValue);

            // При проставлении appendCurrent добавлять любое текущее значение
            if (appendCurrent && displayValue.length > 0 && nextItem === -1) {
                this.unshift(selectValue);
            }

            setCurrentItem(nextItem);
        }
    };
}
